import { Injectable } from '@angular/core';
import { UntypedFormGroup, Validators } from '@angular/forms';
import * as _ from 'lodash';
import { DataService } from '../data/data.service';
import { ValidationService } from '../validation/validation.service';
import { HelpText } from '../../constants';
import { ConfigService } from '../config/config.service';

@Injectable()
export class DataFormService {

	constructor(
		private validationService: ValidationService,
		private dataService: DataService,
		private configService: ConfigService
	) {}

	/**
	 * Takes in external parameters and converts it to a format used internally for the data form component
	 */
	convertDataForForm(
		configObject: any,
		data: any,
		selectOptionsLookup: any,
		includePrimaryFields = true,
		includeAddOnFields = true): Array<FormFieldData> {

		const configFields = this.combineAllFields(configObject, includePrimaryFields, includeAddOnFields);

		const convertedData: FormFieldData[] = [];
		configFields.filter(item => (item.edit !== false))
			.forEach(item => {
				// determine type
				const type = this.determineType(item.validate);

				// field values are left undefined if they don't have a matching value in 'data' object
				let dataValue;
				if (item.isAddOnField && data.flexifields?.[item.prop]) {
					// if add on field (flexifield), then find the value in the flexifields object
					dataValue = data.flexifields[item.prop];
				} else if (data[item.prop]) {
					dataValue = data[item.prop];
				} else if (type === 'boolean') {
					// boolean values need to be considered false if undefined
					dataValue = false;
				}

				// include all fields, even if they won't be shown, in case an 'onlyIf' field is toggled
				convertedData.push({
					prop: item.prop,
					name: item.name,
					validate: item.validate,
					data: dataValue,
					required: this.isRequired(item, data),
					requiredIf: item.requiredIf,
					type: type,
					helpText: this.getHelpText(item.helpText),
					options: type === 'select' || 'combobox' ? this.getSelectOptions(item.prop, selectOptionsLookup) : undefined,
					minVal: item.minVal,
					maxVal: item.maxVal,
					onlyIf: item.onlyIf,
					dateOptions: this.generateDateOptions(item),
					forceLowerCase: item.validate === 'email',
					isAddOnField: !!item.isAddOnField
				});
			}
		);
		return convertedData;
	}

	/**
	 * Infer 'type' from the 'validate' property
	 */
	determineType(validate: string): string {
		let type;
		if (validate === undefined
			|| validate.startsWith('string')
			|| ['', 'email'].includes(validate)
			|| validate === 'alphanumeric3'
			|| validate === 'number3') { // number3 is a country code, which validates as numbers but needs to be a string
			type = 'text';
		} else if (validate.startsWith('date')) {
			type = 'date';
		} else if (validate === 'array') {
			type = 'select';
		} else if (validate === 'comment') {
			// TODO: this needs to be a text area field
		} else if (validate === 'calendarEntries') {
			// TODO: this needs to be an array of dates
		} else {
			type = validate;	// some can be set directly from their validation to the type, like 'boolean' and 'number'
		}
		return type;
	}

	/**
	 * generate date rules object for the date component to specify allowed dates, based on config.
	 * also special handling for 'start' and 'end' date pairs
	 */
	generateDateOptions(item: FormFieldConfiguration, dateMin?: Date, dateMax?: Date): any {
		let needsRule = false;
		// if "date start" or "date end" and then a number, add the day of week value
		let dayOfWeekValue;
		if (item.validate && item.validate.match(/[dateStart|dateEnd][0-6]/)) {
			dayOfWeekValue = item.validate.match(/[0-6]/)[0];
			needsRule = true;
		}

		// if min or max date, add rule
		if (dateMin || dateMax) {
			needsRule = true;
		}

		// only return if any rules need to be applied
		if (needsRule) {
			return { enable: [
				// date component takes a function that returns true if a date should be enabled
				function(date) {
					return (!dayOfWeekValue || date.getDay() === parseInt(dayOfWeekValue, 10))
						&& (!dateMin || date >= dateMin)
						&& (!dateMax || date <= dateMax);
			}]};
		}
	}

	/**
	 * Takes in the complex config object and returns a flat list of fields.
	 */
	combineAllFields(configObject: any, includePrimaryFields: boolean, includeAddOnFields: boolean): Array<FormFieldConfiguration> {
		let primaryFields = [];
		if (includePrimaryFields && Array.isArray(configObject.fields)) {
			primaryFields = configObject.fields;
		}
		// for add-on fields (flexifields), add the field and also add a property so it can be identified later
		const addOnFields = [];
		if (includeAddOnFields && Array.isArray(configObject.objects)) {
			configObject.objects.forEach(obj => {
				obj.fields.forEach(field => {
					addOnFields.push({ ...field, isAddOnField: true });
				});
			});
		}
		return [...primaryFields, ...addOnFields];
	}

	/**
	 * Gets help text for field from a constant list
	 */
	getHelpText(helpTextKey: string): string {
		if (helpTextKey && helpTextKey !== '' && HelpText[helpTextKey]) {
			return HelpText[helpTextKey];
		}
		return undefined;
	}

	/**
	 * retrieve the list of dropdown options matching the prop name
	 */
	getSelectOptions(propName: string, selectOptionsLookup: any): Array<SelectOption> {
		const options = selectOptionsLookup[propName];
		// convert to select type
		return options ? options.map(x => {
			return { id: x, content: x };
		}) : [];
	}

	/**
	 * generate the form object configuration to define the form
	 */
	getFieldsForFormBuilder(displayData: FormFieldData[]): any {
		const newForm = {};
		displayData.forEach(d => {
			newForm[d.prop] = [{
				value: d.data,
				disabled: false		// NOTE: forms can be disabled here, if that's needed at some point
			}, {
				validators: this.getValidators(d)
			}];
		});
		return newForm;
	}

	/**
	 * Retrieves validators from validationService
	 */
	getValidators(dataForField: FormFieldData): Validators[] {
		let dataForFieldModified = dataForField;
		// remove 'date' validation, since the date component will validate date format
		// once all forms are using the data-form component, the date pattern in the validation service should be able to be removed
		if (dataForField.validate && dataForField.validate.startsWith('date')) {
			dataForFieldModified = _.clone(dataForFieldModified);
			dataForFieldModified.validate = '';
		}

		const validators = this.validationService.prepareValidators(dataForFieldModified);

		// for backwards compatibility, removing extra empty element that the
		// validation service pushes onto the beginning of the validators array.
		return validators.slice(1);
	}

	/**
	 * Determine if a field should show on the form based on configuration
	 */
	shouldShow(item, form: UntypedFormGroup, formData): boolean {
		let shouldShow = true;
		let onlyIf = item.onlyIf;
		let isNegated = false;
		if (onlyIf && onlyIf.startsWith('!')) {
			onlyIf = onlyIf.substr(1);
			isNegated = true;
		}
		if (onlyIf) {
			// first check the value of onlyIf in the form , 
			// then check in formData
			// last check if multiple values separatted by -
			if (form && form.controls[onlyIf]) {
				if (form.controls[onlyIf].value === false || form.controls[onlyIf].value === '') {
					shouldShow = false;
				}
			} else if (formData[onlyIf] === false || formData[onlyIf] === '') {
				shouldShow = false;
			} else if (onlyIf.indexOf('-') > 0) {	// onlyIf is multiple conditions such as countries UK-IE...
				const onlyIfs = onlyIf.split('-');
				for (let i=0; i< onlyIfs.length; i++) {
					if (formData[onlyIfs[i]] === true) {
						shouldShow = true;
						break;
					} else if (formData[onlyIfs[i]] === false || formData[onlyIfs[i]] === '') {
						shouldShow = false;
					}
				}
			}
		}
		if (isNegated) {
			shouldShow = !shouldShow;
		}
		return shouldShow;
	}

	/**
	 * Determine if the field is currently required based on the data received.
	 * The config can specify 'required' with an 'x'.
	 * The config can make a field required if another field is 'true' by specifying its prop name in the 'requiredIf' column.
	 * Note: This was copied and modified from data-page-fields-edit,
	 * but it needs to function differently with data-form component, so it can't be shared.
	 */
	isRequired(item: FormFieldConfiguration, data: any): boolean {
		let isRequired = !!item.required;
		const isNegated = (item.requiredIf ? item.requiredIf.startsWith('!') : false);
		if (item.requiredIf !== '') {
			const requiredIf = isNegated ? item.requiredIf.substr(1) : item.requiredIf;
			if (data[requiredIf] === false) {
				isRequired = false;
			} else if (data[requiredIf] === true) {
				isRequired = true;
			}
			isRequired = isNegated ? !isRequired : isRequired;
		}
		return isRequired;
	}

	/**
	 * Format data to return outside of the DataForm component. This returns a new object.
	 */
	formatForReturn(form: UntypedFormGroup, displayData: Array<FormFieldData>, mergeOutputWithInput: boolean, inputData, aclOverwrite: boolean, config): any {
		let returnForm: any = {};
		const flexifieldObject = {};
		// loop through each property on the form
		Object.entries(form.value).forEach((propertyTuple) => {
			const propName = propertyTuple[0];
			const value: any = propertyTuple[1];
			const fieldData = displayData.find(x => x.prop === propName);
			// use empty string for all null/undefined values
			let finalReturnValue;
			if (value === null || value === undefined) {
				finalReturnValue = '';
			} else if (fieldData.type === 'select') {
				finalReturnValue = value.id;
			} else if (fieldData.type === 'date') {
				// if the object is a date type, format to a string. if already a string, just return that value
				finalReturnValue = value instanceof Date ? this.dataService.formatDate(value) : value;
			} else if (fieldData.type === 'number') {
				// number component may return as string. if so, convert to number
				finalReturnValue = typeof value === 'string' ? Number(value) : value;
			} else {
				finalReturnValue = value;
			}

			if (fieldData.isAddOnField) {
				// add on fields are added to a 'flexifields' sub object
				flexifieldObject[propName] = finalReturnValue;
			} else {
				returnForm[propName] = finalReturnValue;
			}
		});

		returnForm.flexifields = flexifieldObject;

		if (mergeOutputWithInput) {
			// merge flexifields first
			returnForm.flexifields = { ...inputData.flexifields, ...returnForm.flexifields };
			// merge data property and form user input. form data should override data property
			returnForm = { ...inputData, ...returnForm };
		}

		returnForm = this.dataService.applyACL(returnForm, this.configService.personas, config.fields, aclOverwrite);

		return returnForm;
	}

	/**
	 * Return what the text of a validaiton error should be to display under a field. Order matters.
	 */
	getInvalidText(form: UntypedFormGroup, prop: string): string {
		const formObjectForValidationService = { form: form };
		if (this.validationService.isRequiredError(formObjectForValidationService, prop)) {
			return 'This field is required';
		} else if (this.validationService.isOutOfRangeError(formObjectForValidationService, prop)) {
			return 'Value outside of required range';
		} else if (this.validationService.isPatternError(formObjectForValidationService, prop)) {
			return 'Does not match required format';
		}
		// not yet implemented: "isMismatchError" is another possible validation check, meant for passwords
	}

	/**
	 * Subscribe to the changes for any field.
	 */
	subscribeToReactiveFields(form: UntypedFormGroup, displayData: Array<FormFieldData>): void {
		displayData.forEach(field => {
			if (field.validate.startsWith('dateStart')) {
				// handle dateStart field updates to update the corresponding dateEnd
				form.get(field.prop).valueChanges.subscribe(value => this.reactToStartDateChange(value, form, displayData));
			} else if (field.requiredIf && field.requiredIf !== '') {
				// handle fields with requiredIf properties to set them as required or not
				const requiredIfPropName = field.requiredIf.startsWith('!') ? field.requiredIf.substr(1) : field.requiredIf;
				const foundMatchingField = form.get(requiredIfPropName);
				if (foundMatchingField) {
					foundMatchingField.valueChanges
						.subscribe(value => this.reactToRequiredIfChanges(field.prop, value, field.requiredIf.startsWith('!'), form, displayData));
				}
			}
		});
	}

	/**
	 * Special handling for start/end dates. If start date is changed, then a matching end date field is searched for.
	 * If a matching end date is found, and it has a date that is equal to or before the start date, then the end date is cleared.
	 * Also, the matching end date's valid dates are updated to not allow overlapping dates.
	 * This system has the limitation that only one start/end date pair can be on a form.
	 */
	reactToStartDateChange(value: Date, form: UntypedFormGroup, displayData: Array<FormFieldData>): void {
		const endDateField = displayData.find(x => x.validate.startsWith('dateEnd'));
		if (endDateField) {
			const endDateValue = form.get(endDateField.prop).value;
			// clear end date if before new start date
			if (value >= endDateValue) {
				form.get(endDateField.prop).setValue(null);
			}
			// date validate format dateEndX-Y: X is day of the week, Y is number of months in the future from the start date
			// this format has the limitation that you can't have a month limitation without a day of the week limitation
			const splitName = endDateField.validate.split('-');
			let endDate;
			if (splitName.length === 2) {
				const plusMonths = parseInt(splitName[1], 10);
				// only set endDate if value is defined. This way, if the start date is being cleared, then it will not have a start/end date
				if (value) {
					endDate = new Date(value.getTime());
					endDate.setMonth(endDate.getMonth() + plusMonths);
				}
			}
			const newDateOptions = this.generateDateOptions(
				<FormFieldConfiguration>{ validate: endDateField.validate },
				value,
				endDate);
			endDateField.dateOptions = newDateOptions;
		}
	}

	/**
	 * React to changes in fields with 'requiredIf' properties. Check to see if fields need to be updated.
	 */
	reactToRequiredIfChanges(
		dependentFieldPropName: string,
		value: any,
		isNegatedValue: boolean,
		form: UntypedFormGroup,
		displayData: Array<FormFieldData>): void {
		// if the value is true, dependent field should become required
		let shouldBeRequired = !!value;
		shouldBeRequired = isNegatedValue ? !shouldBeRequired : shouldBeRequired;
		const customDisplayData = displayData.find(x => x.prop === dependentFieldPropName);
		customDisplayData.required = shouldBeRequired;

		const validators = <any>this.getValidators(customDisplayData);
		form.get(dependentFieldPropName).setValidators(validators);
		form.get(dependentFieldPropName).updateValueAndValidity();
	}
}

// interface to match configuration from config.csv
export interface FormFieldConfiguration {
	prop: string;
	name: string;
	required: boolean;
	requiredIf: string;
	validate: string;
	minVal: string;
	maxVal: string;
	helpText: string;
	onlyIf: string;

	edit: boolean;
	isAddOnField: boolean;
}

// interface for use within data-form object
export interface FormFieldData {
	prop: string;
	name: string;
	required: boolean;
	requiredIf: string;
	validate: string;
	minVal: string;
	maxVal: string;
	helpText: string;
	onlyIf: string;

	data: any;
	type: string;
	options: Array<SelectOption>;
	dateOptions: any;
	forceLowerCase: boolean;
	isAddOnField: boolean;
}

export interface SelectOption {
	content: string;
	id: string;
}
